import React, { useState, Suspense, useCallback } from "react"
import classes from "./Database.module.css"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus, faHeart as faHeartFilled, faList, faGrip, faTrash } from "@fortawesome/free-solid-svg-icons"
import { faHeart } from "@fortawesome/free-regular-svg-icons"

import Gear from "./Gear"
import gearHead from "../../assets/json/parameters/GearInfoHead.json"
import gearClothes from "../../assets/json/parameters/GearInfoClothes.json"
import gearShoes from "../../assets/json/parameters/GearInfoShoes.json"
import {
    useLocalStorage,
    getSkillId,
    getSkillName,
    decrypted_key,
    valid_key,
    getName,
    gearImage,
    brandImage,
    skillImage,
    SkillIDsRev,
    get_ability_labels,
    get_brand_labels,
    get_fresh_labels,
    get_seed_labels,
    SkillIDs,
} from "../../Utils"
import MurmurHash3 from "murmurhash3js"
import { defaultGearList } from "../../DefaultEntries"
import { useTranslation } from "react-i18next"
import DatabaseControls from "./DatabaseControls"

const GearEditor = React.lazy(() => import("./GearEditor"))

const Database = props => {
    const { t } = useTranslation()
    const [gearList, setGearList] = useLocalStorage("gearList", defaultGearList)

    const [newGear, setNewGear] = useState(false)

    const [newGearId, setNewGearId] = useState(-1)
    const [newType, setNewGearType] = useState(-1)
    const [newMain, setNewMain] = useState("Unknown")
    const [newSeed, setNewSeed] = useState(0)
    const [newHistory, setNewHistory] = useState([])
    const [newDrinks, setNewDrinks] = useState([])
    const [showTable, setShowTable] = useState(true)

    const [loadAbility, setLoadAbility] = useState(-1)

    const [abilitiesToShow, setAbilitiesToShow] = useState(get_ability_labels(t))
    const [brandsToShow, setBrandsToShow] = useState(get_brand_labels(t))
    const [favoriteToShow, setFavoriteToShow] = useState(get_fresh_labels(t, classes))
    const [seedsToShow, setSeedsToShow] = useState(get_seed_labels(t))
    const [rerender, setRerender] = useState(false)

    const newGearHandler = (gearId, gearType) => {
        setNewGearId(gearId)
        setNewGearType(gearType)
        const gearList = gearType === 0 ? gearHead : gearType === 1 ? gearClothes : gearShoes
        const pgear = gearList.filter(elem => elem.Id === gearId)
        setNewMain(pgear[0].Skill)
        setNewSeed(0)
    }

    const cancelGearEdit = () => {
        setNewGear(false)
        setNewGearId(-1)
        setNewGearType(-1)
        setNewMain("Unknown")
        setNewSeed(0)
        setLoadAbility(-1)
        setNewHistory([])
        setNewDrinks([])
    }

    const add_new_gear_handler = elem => {
        const gearKey = newType === 0 ? "HaveGearHeadMap" : newType === 1 ? "HaveGearClothesMap" : "HaveGearShoesMap"
        const new_dict = { ...gearList }
        const fav = gearList[gearKey] ? (gearList[gearKey][newGearId] ? gearList[gearKey][newGearId].Favorite ?? false : false) : false
        new_dict[gearKey][newGearId] = {
            ExSkillArray: newHistory,
            ExDrinksArray: newDrinks,
            MainSkill: getSkillId(newMain),
            RandomContext: newSeed,
            Favorite: fav,
        }
        setGearList(new_dict)
        cancelGearEdit()
    }

    const set_new_gear_handler = (p_gear_id, p_type, p_main, p_history, p_historyDrinks, p_seed) => {
        setNewGearId(p_gear_id)
        setNewGearType(p_type)
        setNewMain(getSkillName(p_main))
        setNewSeed(p_seed)
        setNewHistory([...p_history])
        setNewDrinks([...p_historyDrinks])
        setNewGear(true)
    }

    const checkSeedHandler = event => {
        let inputstr = event.target.value.toUpperCase()
        if (inputstr.substring(0, 2) === "0X") {
            inputstr = inputstr.substring(2)
        }
        inputstr = inputstr.replace(/[^0-9A-F]/g, "")
        while (inputstr.length > 0 && inputstr.substring(0, 1) === "0") {
            inputstr = inputstr.slice(1)
        }
        if (inputstr.length === 0) {
            inputstr = "0"
        }
        inputstr = "0x" + inputstr
        inputstr = inputstr.substring(0, 10) // cut off too many characters
        const as_int = parseInt(inputstr.substring(2), 16)
        setNewSeed(as_int)
    }

    const loadAbilityHandler = event => {
        setLoadAbility(event)
    }

    const abilityOverwriteHandler = (position, ability) => {
        if (position === 0) {
            setNewMain(getSkillName(ability))
        } else {
            const pos = ((position - 1) / 2) >>> 0
            if (position & 1) {
                // ability
                if (newHistory.length === pos) {
                    setNewHistory(currentHistory => {
                        return [...currentHistory, ability]
                    })
                    setNewDrinks(currentDrinks => {
                        return [...currentDrinks, 255]
                    })
                } else {
                    const new_history = [...newHistory]
                    const new_drinks = [...newDrinks]
                    new_history[pos] = ability
                    // clean up
                    while (new_history[new_history.length - 1] === 255 && new_drinks[new_drinks.length - 1] === 255) {
                        new_history.splice(new_history.length - 1, 1)
                        new_drinks.splice(new_drinks.length - 1, 1)
                    }
                    setNewHistory(new_history)
                    setNewDrinks(new_drinks)
                }
            } else {
                // drink
                if (newHistory.length === pos) {
                    setNewHistory(currentHistory => {
                        return [...currentHistory, 255]
                    })
                    setNewDrinks(currentDrinks => {
                        return [...currentDrinks, ability]
                    })
                } else {
                    const new_history = [...newHistory]
                    const new_drinks = [...newDrinks]
                    new_drinks[pos] = ability
                    //cleanup
                    while (new_history[new_history.length - 1] === 255 && new_drinks[new_drinks.length - 1] === 255) {
                        new_history.splice(new_history.length - 1, 1)
                        new_drinks.splice(new_drinks.length - 1, 1)
                    }
                    setNewHistory(new_history)
                    setNewDrinks(new_drinks)
                }
            }
        }
        setLoadAbility(-1)
    }

    const deleteData = useCallback(() => {
        if (!window.confirm(t("misc:Database.DeleteConfirmation"))) return

        const gearKey = newType === 0 ? "HaveGearHeadMap" : newType === 1 ? "HaveGearClothesMap" : "HaveGearShoesMap"
        const new_dict = { ...gearList }
        if (newGearId in new_dict[gearKey]) {
            delete new_dict[gearKey][newGearId]
        }
        setGearList(new_dict)
        cancelGearEdit()
    }, [setGearList, gearList, newGearId, newType, t])

    const deleteEntry = useCallback(
        (gearId, type) => {
            if (!window.confirm(t("misc:Database.DeleteConfirmation"))) return

            const gearKey = type === 0 ? "HaveGearHeadMap" : type === 1 ? "HaveGearClothesMap" : "HaveGearShoesMap"
            const new_dict = { ...gearList }
            if (gearId in new_dict[gearKey]) {
                delete new_dict[gearKey][gearId]
            }
            setGearList(new_dict)
            cancelGearEdit()
        },
        [setGearList, gearList, t]
    )

    const generateSeedHandler = () => {
        const gearKey = newType === 0 ? "Head" : newType === 1 ? "Clothes" : "Shoes"
        const new_seed =
            newType === 1 && newGearId === 26000
                ? MurmurHash3.x86.hash32(`${decrypted_key()}_${gearKey}_${newGearId}_JUEA-00100`) >>> 0
                : MurmurHash3.x86.hash32(`${decrypted_key()}_${gearKey}_${newGearId}`) >>> 0
        setNewSeed(new_seed)
    }

    const likeGear = (gearId, old, type, new_like) => {
        const gearKey = type === 0 ? "HaveGearHeadMap" : type === 1 ? "HaveGearClothesMap" : "HaveGearShoesMap"
        const new_dict = { ...gearList }
        new_dict[gearKey][gearId] = {
            ExSkillArray: old.ExSkillArray,
            ExDrinksArray: old.ExDrinksArray,
            MainSkill: old.MainSkill,
            RandomContext: old.RandomContext,
            Favorite: new_like,
        }
        setGearList(new_dict)
        cancelGearEdit()
    }

    const get_entry_of_table = (gear, body_length, v, namespace, type) => {
        return (
            <tr key={`${gear.Id}_${type}`} className={(body_length & 1) === 0 ? classes.TDEven : classes.TDOdd}>
                <td
                    className={classes.HoverPointer}
                    onClick={() => set_new_gear_handler(gear.Id, type, v.MainSkill, v.ExSkillArray, v.ExDrinksArray, v.RandomContext)}
                >
                    <div className={classes.FlexContent}>
                        <img src={gearImage(gear.__RowId)} alt={gear.__RowId} className={classes.Gear} />
                        <span className={classes.displayText}>{getName(t, namespace, gear)}</span>
                    </div>
                </td>
                <td
                    className={classes.HoverPointer}
                    onClick={() => set_new_gear_handler(gear.Id, type, v.MainSkill, v.ExSkillArray, v.ExDrinksArray, v.RandomContext)}
                >
                    <div className={classes.FlexContent}>
                        <img src={brandImage(gear.Brand)} alt={gear.Brand} className={classes.Brand} />
                        <span className={classes.displayText}>{t("translation:CommonMsg.Gear.GearBrandName." + gear.Brand)}</span>
                    </div>
                </td>
                <td
                    className={classes.HoverPointer}
                    onClick={() => set_new_gear_handler(gear.Id, type, v.MainSkill, v.ExSkillArray, v.ExDrinksArray, v.RandomContext)}
                >
                    <div className={classes.FlexContent}>
                        <img
                            src={skillImage(SkillIDsRev[v.MainSkill] ? SkillIDsRev[v.MainSkill] : "Unknown")}
                            className={classes.MainAbility}
                            alt={v.MainSkill}
                        />
                        <span className={classes.displayText}>
                            {t(
                                "translation:CommonMsg.Gear.GearPowerName." +
                                    (SkillIDsRev[v.MainSkill] ? SkillIDsRev[v.MainSkill] : "Unknown")
                            )}
                        </span>
                    </div>
                </td>
                <td
                    className={classes.HoverPointer}
                    onClick={() => set_new_gear_handler(gear.Id, type, v.MainSkill, v.ExSkillArray, v.ExDrinksArray, v.RandomContext)}
                >{`0x${(v.RandomContext >>> 0).toString(16).padStart(8, "0")}`}</td>
                <td>
                    {v.Favorite ? (
                        <div className={classes.liked} onClick={() => likeGear(gear.Id, v, type, false)}>
                            <FontAwesomeIcon icon={faHeartFilled} className="fa-fw" />
                        </div>
                    ) : (
                        <div className={classes.likebutton} onClick={() => likeGear(gear.Id, v, type, true)}>
                            <FontAwesomeIcon icon={faHeart} className="fa-fw" />
                        </div>
                    )}
                </td>
                <td>
                    <div className={classes.HoverPointer} onClick={() => deleteEntry(gear.Id, type)}>
                        <FontAwesomeIcon icon={faTrash} className="fa-fw" />
                    </div>
                </td>
            </tr>
        )
    }

    const filterResults = () => {
        setRerender(val => !val)
    }

    const filterFunction = (gear, v) => {
        if (seedsToShow.length === 1) {
            if (seedsToShow[0].value === (v.RandomContext === 0)) {
                return false
            }
        }
        if (favoriteToShow.length === 1) {
            if (favoriteToShow[0].value && v.Favorite === undefined) {
                return false
            }
            if (v.Favorite !== undefined && favoriteToShow[0].value !== v.Favorite) {
                return false
            }
        }
        const gear_brand = brandsToShow.map(e => e.value)
        if (gear_brand.length > 0 && !gear_brand.includes(gear.Brand)) {
            return false
        }
        const skillToShow = abilitiesToShow.map(e => SkillIDs[e.value])
        if (skillToShow.length > 0 && !skillToShow.includes(v.MainSkill)) {
            return false
        }
        return true
    }

    const get_body = useCallback(() => {
        const body = []
        for (const [k, v] of Object.entries(gearList.HaveGearHeadMap)) {
            const gear = gearHead.filter(elem => elem.Id === parseInt(k))[0]
            if (gear !== undefined && filterFunction(gear, v)) {
                body.push(get_entry_of_table(gear, body.length, v, "CommonMsg.Gear.GearName_Head", 0))
            }
        }
        for (const [k, v] of Object.entries(gearList.HaveGearClothesMap)) {
            const gear = gearClothes.filter(elem => elem.Id === parseInt(k))[0]
            if (gear !== undefined && filterFunction(gear, v)) {
                body.push(get_entry_of_table(gear, body.length, v, "CommonMsg.Gear.GearName_Clothes", 1))
            }
        }
        for (const [k, v] of Object.entries(gearList.HaveGearShoesMap)) {
            const gear = gearShoes.filter(elem => elem.Id === parseInt(k))[0]
            if (gear !== undefined && filterFunction(gear, v)) {
                body.push(get_entry_of_table(gear, body.length, v, "CommonMsg.Gear.GearName_Shoes", 2))
            }
        }
        return body
        // eslint-disable-next-line
    }, [rerender, gearList, get_entry_of_table, filterFunction])

    if (showTable) {
        return (
            <>
                <h1>
                    Splatoon 3 - {t("misc:Navigation.GearDatabase")}{" "}
                    <FontAwesomeIcon icon={faGrip} className="fa-fw" onClick={() => setShowTable(false)} />
                </h1>
                <div className={classes.SettingsContent}>
                    <DatabaseControls
                        abilitiesToShow={abilitiesToShow}
                        setAbilitiesToShow={setAbilitiesToShow}
                        brandsToShow={brandsToShow}
                        setBrandsToShow={setBrandsToShow}
                        favoriteToShow={favoriteToShow}
                        setFavoriteToShow={setFavoriteToShow}
                        seedsToShow={seedsToShow}
                        setSeedsToShow={setSeedsToShow}
                        filterResults={filterResults}
                        setNewGear={setNewGear}
                    />
                </div>
                <div className={classes.SettingsContent}>
                    <div className={classes.Wrapper}>
                        <Suspense fallback={false}>
                            <GearEditor
                                show={newGear}
                                onHide={() => cancelGearEdit()}
                                onSave={add_new_gear_handler}
                                onDelete={deleteData}
                                currentGearList={gearList}
                                newGearId={newGearId}
                                setNewGearId={setNewGearId}
                                newType={newType}
                                setNewGearType={setNewGearType}
                                newMain={newMain}
                                setNewMain={setNewMain}
                                newSeed={newSeed}
                                setNewSeed={setNewSeed}
                                newGearHandler={newGearHandler}
                                seedValidator={checkSeedHandler}
                                loadAbility={loadAbility}
                                loadAbilityHandler={loadAbilityHandler}
                                abilityOverwriteHandler={abilityOverwriteHandler}
                                history={newHistory}
                                historyDrinks={newDrinks}
                                keyset={valid_key() && newSeed === 0}
                                generateSeedHandler={generateSeedHandler}
                            />
                        </Suspense>
                        <table className={classes.Table}>
                            <thead>
                                <tr>
                                    <th className={classes.TableHead}>
                                        {t("translation:LayoutMsg.Cmn_Menu_00.L_Player_00-T_BlackText_00")}
                                    </th>
                                    <th className={classes.TableHead}>{t("translation:LayoutMsg.Cmn_CstBase_00.002")}</th>
                                    <th className={classes.TableHead}>{t("translation:LayoutMsg.Cmn_CstBase_00.001")}</th>
                                    <th className={classes.TableHead}>{t("misc:Common.Seed")}</th>
                                    <th className={classes.TableHead}>{t("translation:LayoutMsg.Cmn_CstBase_00.000")}</th>
                                    <th className={classes.TableHead}>{t("misc:Common.Delete")}</th>
                                </tr>
                            </thead>
                            <tbody>{get_body()}</tbody>
                        </table>
                    </div>
                </div>
            </>
        )
    } else {
        return (
            <>
                <h1>
                    Splatoon 3 - {t("misc:Navigation.GearDatabase")}{" "}
                    <FontAwesomeIcon icon={faList} className="fa-fw" onClick={() => setShowTable(true)} />
                </h1>

                <div className={classes.SettingsContent}>
                    <div className={classes.Wrapper}>
                        <Suspense fallback={false}>
                            <GearEditor
                                show={newGear}
                                onHide={() => cancelGearEdit()}
                                onSave={add_new_gear_handler}
                                onDelete={deleteData}
                                currentGearList={gearList}
                                newGearId={newGearId}
                                setNewGearId={setNewGearId}
                                newType={newType}
                                setNewGearType={setNewGearType}
                                newMain={newMain}
                                setNewMain={setNewMain}
                                newSeed={newSeed}
                                setNewSeed={setNewSeed}
                                newGearHandler={newGearHandler}
                                seedValidator={checkSeedHandler}
                                loadAbility={loadAbility}
                                loadAbilityHandler={loadAbilityHandler}
                                abilityOverwriteHandler={abilityOverwriteHandler}
                                history={newHistory}
                                historyDrinks={newDrinks}
                                keyset={valid_key() && newSeed === 0}
                                generateSeedHandler={generateSeedHandler}
                            />
                        </Suspense>
                        <div className={classes.Card} onClick={() => setNewGear(true)}>
                            <div className={classes.CardDark}>
                                <div className={classes.CardContent}>{t("misc:Database.AddNewGear")}</div>
                                <div className={classes.CardBox}>
                                    <button className={classes.Button}>
                                        <div className={classes.ButtonH1}>
                                            <FontAwesomeIcon icon={faPlus} className="fa-fw" />
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>
                        {Object.entries(gearList.HaveGearHeadMap).map(([k, v], idx) => {
                            return (
                                <Gear
                                    key={`${k}-0`}
                                    history={v.ExSkillArray}
                                    historyDrinks={v.ExDrinksArray}
                                    seed={v.RandomContext}
                                    main={v.MainSkill}
                                    type={0}
                                    gear_id={parseInt(k)}
                                    onClick={set_new_gear_handler}
                                />
                            )
                        })}
                        {Object.entries(gearList.HaveGearClothesMap).map(([k, v], idx) => {
                            return (
                                <Gear
                                    key={`${k}-1`}
                                    history={v.ExSkillArray}
                                    historyDrinks={v.ExDrinksArray}
                                    seed={v.RandomContext}
                                    main={v.MainSkill}
                                    type={1}
                                    gear_id={parseInt(k)}
                                    onClick={set_new_gear_handler}
                                />
                            )
                        })}
                        {Object.entries(gearList.HaveGearShoesMap).map(([k, v], idx) => {
                            return (
                                <Gear
                                    key={`${k}-2`}
                                    history={v.ExSkillArray}
                                    historyDrinks={v.ExDrinksArray}
                                    seed={v.RandomContext}
                                    main={v.MainSkill}
                                    type={2}
                                    gear_id={parseInt(k)}
                                    onClick={set_new_gear_handler}
                                />
                            )
                        })}
                    </div>
                </div>
            </>
        )
    }
}

export default Database
